/* ─── Page-level ──────────────────────────────────────── */

.page {
  min-height: 100vh;
  background: var(--mantine-color-body);
  color: var(--mantine-color-text);
}

/* ─── NAV ─────────────────────────────────────────────── */

.nav {
  position: sticky;
  top: 0;
  z-index: 50;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  background: light-dark(
    color-mix(in srgb, white 78%, transparent),
    color-mix(in srgb, var(--mantine-color-dark-7) 82%, transparent)
  );
  border-bottom: 1px solid var(--mantine-color-default-border);
}

.brandIcon {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: light-dark(var(--mantine-color-teal-7), var(--mantine-color-teal-5));
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(3, 1fr);
  gap: 2px;
  padding: 4px;
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.08),
    0 0 0 1px light-dark(transparent, rgba(255, 255, 255, 0.05));
}

.brandIconCell {
  background: white;
  border-radius: 1px;
}

.brandWord {
  font-weight: 700;
  font-size: 1.125rem;
  letter-spacing: -0.015em;
  color: light-dark(var(--mantine-color-teal-7), var(--mantine-color-teal-3));
}

.navLink {
  color: light-dark(var(--mantine-color-gray-7), var(--mantine-color-gray-3));
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  transition: color 120ms ease;
}
.navLink:hover {
  color: light-dark(var(--mantine-color-teal-7), var(--mantine-color-teal-3));
  text-decoration: none;
}

/* ─── HERO ────────────────────────────────────────────── */

.hero {
  position: relative;
  overflow: hidden;
  background: light-dark(
    linear-gradient(180deg, color-mix(in srgb, var(--mantine-color-teal-0) 70%, transparent) 0%, transparent 100%),
    linear-gradient(180deg, color-mix(in srgb, var(--mantine-color-teal-9) 22%, transparent) 0%, transparent 100%)
  );
}

/* Soft radial glow behind the headline area — gives depth without being loud */
.hero::before {
  content: "";
  position: absolute;
  inset: 0;
  background:
    radial-gradient(
      ellipse 60% 50% at 18% 30%,
      light-dark(rgba(42, 170, 138, 0.12), rgba(42, 170, 138, 0.18)),
      transparent 60%
    ),
    radial-gradient(
      ellipse 40% 40% at 78% 20%,
      light-dark(rgba(255, 138, 76, 0.08), rgba(255, 138, 76, 0.12)),
      transparent 60%
    );
  pointer-events: none;
  z-index: 0;
}

.hero > * {
  position: relative;
  z-index: 1;
}

/* Solid adaptive color — text-clipped gradient ("background-clip: text"
   + "color: transparent") is fragile on Mantine <Text inherit>: the
   inherit prop re-applies the parent Title color, the gradient renders
   as a solid block, and the glyphs vanish. Solid color is reliable. */
.heroAccent {
  color: light-dark(var(--mantine-color-teal-7), var(--mantine-color-teal-3));
  font-weight: 700;
}

.heroSubtext {
  color: light-dark(var(--mantine-color-gray-7), var(--mantine-color-gray-4));
  font-size: 1.125rem;
  line-height: 1.55;
}

.heroCheck {
  color: light-dark(var(--mantine-color-gray-7), var(--mantine-color-gray-4));
  font-size: 0.875rem;
}

.heroCheckIcon {
  color: light-dark(var(--mantine-color-teal-6), var(--mantine-color-teal-4));
  flex-shrink: 0;
}

/* Microcopy directly below the primary CTA. Lighter than body text but
   still readable, sized to feel "in-line with the button" not standalone. */
.heroSubMicrocopy {
  color: light-dark(var(--mantine-color-gray-6), var(--mantine-color-gray-5));
  font-size: 0.8rem;
  font-weight: 500;
  letter-spacing: 0.01em;
}

/* Hero "browser" mock */
.mockup {
  background: light-dark(white, var(--mantine-color-dark-6));
  border: 1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-4));
  border-radius: 12px;
  overflow: hidden;
  box-shadow: light-dark(0 24px 48px -12px rgba(31, 124, 106, 0.18), 0 24px 48px -12px rgba(0, 0, 0, 0.6));
}

.mockupHeader {
  padding: 12px;
  border-bottom: 1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-4));
  background: light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-7));
}

.mockupAddress {
  font-size: 0.75rem;
  color: light-dark(var(--mantine-color-gray-6), var(--mantine-color-gray-5));
  margin-left: 8px;
}

.miniStat {
  background: light-dark(white, var(--mantine-color-dark-7));
  border: 1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-4));
  border-radius: 6px;
  padding: 10px;
  text-align: center;
}

.miniStatValue {
  font-weight: 700;
  font-size: 1.5rem;
  color: light-dark(var(--mantine-color-teal-7), var(--mantine-color-teal-3));
  line-height: 1.1;
}

.miniStatLabel {
  font-size: 0.7rem;
  color: light-dark(var(--mantine-color-gray-6), var(--mantine-color-gray-5));
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 2px;
}

.recentBookingCard {
  background: light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-7));
  border: 1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-4));
  border-radius: 6px;
  padding: 10px;
}

.recentBookingLabel {
  font-size: 0.65rem;
  font-weight: 700;
  color: light-dark(var(--mantine-color-gray-6), var(--mantine-color-gray-5));
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.floatingCard {
  background: light-dark(white, var(--mantine-color-dark-6));
  border: 1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-4));
  border-radius: 10px;
  padding: 10px;
  box-shadow: light-dark(0 8px 24px -6px rgba(31, 124, 106, 0.18), 0 8px 24px -6px rgba(0, 0, 0, 0.5));
  transition: transform 220ms ease;
}

.floatingCard:hover {
  transform: translateY(-2px);
}

/* ─── Section utilities ───────────────────────────────── */

/* Subtle alternating section — works in both light and dark.
   Light: very pale grey; Dark: slightly raised dark surface (NOT a bright slab). */
.sectionAlt {
  background: light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-8));
  border-top: 1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-5));
  border-bottom: 1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-5));
  padding-top: 80px;
  padding-bottom: 80px;
}

.sectionTitle {
  font-family: var(--font-display);
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  font-weight: 700;
  letter-spacing: -0.015em;
  color: light-dark(var(--mantine-color-gray-9), var(--mantine-color-gray-1));
}

.sectionSubtitle {
  color: light-dark(var(--mantine-color-gray-6), var(--mantine-color-gray-5));
  font-size: 1rem;
  max-width: 560px;
  text-align: center;
}

/* ─── STATS ───────────────────────────────────────────── */

.statValue {
  font-weight: 800;
  letter-spacing: -0.02em;
  color: light-dark(var(--mantine-color-teal-7), var(--mantine-color-teal-3));
}

.statLabel {
  color: light-dark(var(--mantine-color-gray-6), var(--mantine-color-gray-5));
  font-size: 0.875rem;
  text-align: center;
}

/* ─── Cards (features, indo advantages, pricing) ──────── */

.card {
  background: light-dark(white, var(--mantine-color-dark-7));
  border: 1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-5));
  border-radius: 10px;
  padding: 24px;
  transition:
    transform 220ms ease,
    box-shadow 220ms ease,
    border-color 220ms ease;
}

.card:hover {
  transform: translateY(-3px);
  border-color: light-dark(var(--mantine-color-teal-3), var(--mantine-color-teal-7));
  box-shadow: light-dark(0 16px 32px -12px rgba(31, 124, 106, 0.16), 0 16px 32px -12px rgba(0, 0, 0, 0.5));
}

.cardCategory {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: light-dark(var(--mantine-color-teal-7), var(--mantine-color-teal-3));
  text-transform: uppercase;
}

.cardTitle {
  font-size: 1.125rem;
  font-weight: 700;
  margin-bottom: 6px;
  color: light-dark(var(--mantine-color-gray-9), var(--mantine-color-gray-1));
}

.cardDesc {
  font-size: 0.875rem;
  color: light-dark(var(--mantine-color-gray-6), var(--mantine-color-gray-4));
  line-height: 1.55;
  margin-bottom: 14px;
}

.cardEmoji {
  font-size: 2rem;
  line-height: 1;
  margin-bottom: 10px;
  filter: light-dark(none, saturate(1.1));
}

.cardBullet {
  font-size: 0.875rem;
  color: light-dark(var(--mantine-color-gray-7), var(--mantine-color-gray-3));
}

/* ─── PRICING ─────────────────────────────────────────── */

/* Compound selector beats `.card` specificity (1+1 > 1) without needing !important */
.card.pricingPopular {
  border-color: light-dark(var(--mantine-color-teal-6), var(--mantine-color-teal-5));
  box-shadow: light-dark(0 16px 48px -12px rgba(31, 124, 106, 0.22), 0 16px 48px -12px rgba(42, 170, 138, 0.32));
  position: relative;
}

.pricingPopular::after {
  content: "";
  position: absolute;
  inset: -1px;
  border-radius: 10px;
  padding: 1px;
  background: linear-gradient(
    135deg,
    light-dark(var(--mantine-color-teal-5), var(--mantine-color-teal-4)),
    transparent 60%
  );
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}

.priceAmount {
  font-weight: 800;
  font-size: 2.25rem;
  color: light-dark(var(--mantine-color-gray-9), var(--mantine-color-gray-0));
  letter-spacing: -0.02em;
}

.pricePeriod {
  color: light-dark(var(--mantine-color-gray-6), var(--mantine-color-gray-5));
  font-size: 0.875rem;
}

/* ─── FAQ ─────────────────────────────────────────────── */

.faqAnswer {
  color: light-dark(var(--mantine-color-gray-7), var(--mantine-color-gray-4));
  font-size: 0.9rem;
  line-height: 1.6;
}

/* ─── FINAL CTA ───────────────────────────────────────── */

.finalCta {
  position: relative;
  overflow: hidden;
  padding: 80px 0;
  /* Toned-down gradient — same hue, lower intensity in dark to avoid retina-burn */
  background: light-dark(
    linear-gradient(135deg, var(--mantine-color-teal-6) 0%, var(--mantine-color-orange-5) 100%),
    linear-gradient(135deg, var(--mantine-color-teal-8) 0%, var(--mantine-color-orange-7) 100%)
  );
  color: white;
}

.finalCta::before {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse 80% 60% at 50% 0%, rgba(255, 255, 255, 0.15), transparent 70%);
  pointer-events: none;
}

.finalCta > * {
  position: relative;
}

/* ─── FOOTER ──────────────────────────────────────────── */

.footer {
  background: light-dark(var(--mantine-color-dark-8), var(--mantine-color-dark-9));
  color: light-dark(var(--mantine-color-gray-3), var(--mantine-color-gray-4));
  padding: 60px 0 32px;
  border-top: 1px solid light-dark(var(--mantine-color-dark-6), var(--mantine-color-dark-7));
}

.footerHeading {
  font-weight: 700;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  color: white;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.footerText {
  color: light-dark(var(--mantine-color-gray-4), var(--mantine-color-gray-5));
  font-size: 0.875rem;
}

.footerLink {
  color: light-dark(var(--mantine-color-gray-3), var(--mantine-color-gray-4));
  font-size: 0.875rem;
  text-decoration: none;
  transition: color 120ms ease;
}
.footerLink:hover {
  color: light-dark(var(--mantine-color-teal-3), var(--mantine-color-teal-4));
  text-decoration: none;
}

.footerDivider {
  border-top: 1px solid light-dark(var(--mantine-color-dark-5), var(--mantine-color-dark-7));
  margin: 24px 0 16px;
}

.footerCopy {
  color: light-dark(var(--mantine-color-gray-5), var(--mantine-color-gray-6));
  font-size: 0.75rem;
}

/* ─── CLIENT STRIP ────────────────────────────────────── */

.clientName {
  font-size: 0.875rem;
  font-weight: 600;
  color: light-dark(var(--mantine-color-gray-4), var(--mantine-color-gray-6));
  letter-spacing: 0.01em;
  transition: color 150ms ease;
}

.clientName:hover {
  color: light-dark(var(--mantine-color-teal-6), var(--mantine-color-teal-4));
}

/* ─── HOW IT WORKS ────────────────────────────────────── */

.stepNum {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: light-dark(var(--mantine-color-teal-0), rgba(42, 170, 138, 0.1));
  border: 2px solid light-dark(var(--mantine-color-teal-3), var(--mantine-color-teal-8));
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 1.2rem;
  color: light-dark(var(--mantine-color-teal-6), var(--mantine-color-teal-4));
  letter-spacing: -0.02em;
  flex-shrink: 0;
}

.stepConnector {
  position: absolute;
  top: 20px;
  right: -28px;
  font-size: 1.25rem;
  font-weight: 300;
  color: light-dark(var(--mantine-color-gray-3), var(--mantine-color-gray-7));
  pointer-events: none;
  z-index: 1;
}

/* ─── TESTIMONIALS ────────────────────────────────────── */

.testimonialCard {
  background: light-dark(white, var(--mantine-color-dark-7));
  border: 1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-5));
  border-radius: 12px;
  padding: 28px;
  position: relative;
  transition:
    transform 220ms ease,
    box-shadow 220ms ease;
}

.testimonialCard:hover {
  transform: translateY(-3px);
  box-shadow: light-dark(0 16px 32px -12px rgba(31, 124, 106, 0.14), 0 16px 32px -12px rgba(0, 0, 0, 0.45));
}

.testimonialCard::before {
  content: "\201C";
  position: absolute;
  top: 14px;
  left: 22px;
  font-size: 3.5rem;
  line-height: 1;
  color: light-dark(var(--mantine-color-teal-1), rgba(42, 170, 138, 0.2));
  font-family: Georgia, "Times New Roman", serif;
  pointer-events: none;
  user-select: none;
}

.testimonialQuote {
  font-size: 0.9rem;
  line-height: 1.65;
  color: light-dark(var(--mantine-color-gray-7), var(--mantine-color-gray-4));
  font-style: italic;
  padding-top: 28px;
}

.testimonialAvatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: light-dark(var(--mantine-color-teal-0), rgba(42, 170, 138, 0.15));
  color: light-dark(var(--mantine-color-teal-7), var(--mantine-color-teal-4));
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  flex-shrink: 0;
  border: 1px solid light-dark(var(--mantine-color-teal-2), rgba(42, 170, 138, 0.25));
}
